<template>
  <validation-observer ref="confirmForm">
    <b-form @submit.prevent="confirmReservation">
      <customer-selector
        v-if="isGuardian"
        v-model="selectedCustomerId"
        :customers="customers"
      />
      <template v-else-if="!isLoggedIn">
        <!-- Registration fields (default) -->
        <div v-if="!showLogin">

          <h4>{{ $t('auth.register.reservation-required-title') }}</h4>
          <p>{{ $t('auth.register.reservation-required-description') }}</p>

          <b-form-group
            :label="$t('auth.register.firstname')"
            label-for="first-name"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('auth.register.firstname')"
              rules="required"
            >
              <b-form-input
                id="first-name"
                v-model="guest.firstName"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('auth.register.lastname')"
            label-for="last-name"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('auth.register.lastname')"
              rules="required"
            >
              <b-form-input
                id="last-name"
                v-model="guest.lastName"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('auth.register.phone')"
            label-for="phone"
          >
            <validation-provider
              #default="{ errors, touched, dirty }"
              rules="required"
              :name="$t('auth.register.phone')"
              mode="lazy"
            >
              <vue-tel-input
                v-model="guest.phone"
                mode="international"
                default-country="PL"
                :dropdown-options="{ showFlags: true, showDialCodeInSelection: false, showSearchBox: true }"
                :input-options="{ placeholder: '', showDialCode: false }"
                :valid-characters-only="true"
                :class="{ 'border-danger': ((touched || dirty || phoneTouched) && (!!errors.length || phoneNumberValid === false)) }"
                @validate="handlePhoneValidation"
                @input="phoneTouched = true"
                @blur="phoneTouched = true"
              />
              <small
                v-if="(touched || dirty || phoneTouched) && errors.length"
                class="text-danger"
              >{{ errors[0] }}</small>
              <small
                v-else-if="(touched || dirty || phoneTouched) && phoneNumberValid === false"
                class="text-danger"
              >{{ $t('validations.messages.phone-number-invalid') }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('auth.your-email')"
            label-for="email"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('auth.your-email')"
              rules="required|email"
            >
              <b-form-input
                id="email"
                v-model="guest.email"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('auth.password')"
            label-for="password"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('auth.password')"
              rules="required|password"
            >
              <b-form-input
                id="password"
                v-model="guest.password"
                type="password"
                autocomplete="new-password"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group>
            <validation-provider
              #default="{ errors }"
              name="accept"
              :rules="{ required: { allowFalse: false }}"
            >
              <b-form-checkbox
                v-model="acceptRegulations"
                name="accept"
              >
                {{ $t('auth.register.i-agree') }}
                <b-link
                  href="https://totedi.com/legal/regulamin.pdf"
                  target="_blank"
                >
                  {{ $t('auth.register.regulations') }}
                </b-link>
                {{ $t('shared.and') }}
                <b-link
                  href="https://totedi.com/privacy-policy"
                  target="_blank"
                >
                  {{ $t('auth.register.privacy-policy') }}
                </b-link>
              </b-form-checkbox>
              <small
                v-if="errors.length"
                class="text-danger"
              >{{ $t('auth.register.acceptance-is-required') }}</small>
            </validation-provider>
          </b-form-group>
        </div>

        <!-- Inline login form (toggleable) -->
        <div v-else>
          <b-alert
            v-if="accountExistsMessage"
            show
            variant="info"
            class="mb-2 p-1"
          >
            {{ accountExistsMessage }}
          </b-alert>

          <validation-observer ref="loginForm">
            <b-form @submit.prevent>
              <b-form-group
                :label="$t('auth.your-email')"
                label-for="login-email-inline"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('auth.your-email')"
                  rules="required|email"
                >
                  <b-form-input
                    id="login-email-inline"
                    v-model="loginEmail"
                    :state="errors.length ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group
                :label="$t('auth.password')"
                label-for="login-password-inline"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('auth.password')"
                  rules="required"
                >
                  <b-form-input
                    id="login-password-inline"
                    v-model="loginPassword"
                    type="password"
                    autocomplete="current-password"
                    :state="errors.length ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-form>
          </validation-observer>
        </div>
      </template>

      <template v-if="isLoggedIn && !isGuardian">
        <h4 class="mb-2">
          {{ $t('settings.profile.reservation.check-and-confirm') }}
        </h4>
        <p class="mb-1">
          {{ $t('settings.profile.reservation.before-confirm') }}
        </p>

        <ul class="mb-3">
          <li>{{ $t('settings.profile.reservation.check-activity') }}</li>
          <li>{{ $t('settings.profile.reservation.check-datetime') }}</li>
        </ul>
      </template>

      <div
        id="action-buttons"
        class="d-flex justify-content-between align-items-center"
      >
        <div class="d-flex align-items-center">
          <b-button
            v-if="!showLogin"
            type="submit"
            variant="primary"
            :disabled="processing"
            class="mr-1"
          >
            <b-spinner
              v-show="processing"
              small
            />
            {{ $t(isLoggedIn ? 'settings.profile.reservation.confirm' : 'auth.register.sign-up-and-reserve') }}
          </b-button>

          <!-- Inline login toggle/submit button -->
          <b-button
            v-if="!isLoggedIn"
            :variant="showLogin ? 'primary' : 'outline-primary'"
            :disabled="loginProcessing"
            class="mr-1"
            type="button"
            @click="loginButtonClick"
          >
            <b-spinner
              v-show="loginProcessing"
              small
            />
            {{ $t('auth.login.sign-in') }}
          </b-button>
        </div>

        <b-button
          v-if="!showLogin"
          variant="flat-secondary"
          :disabled="processing || loginProcessing"
          @click="$emit('back')"
        >
          {{ $t('settings.profile.reservation.btn-back') }}
        </b-button>
        <b-button
          v-else
          variant="flat-secondary"
          @click="showLogin = false; accountExistsMessage = null;"
        >
          {{ $t('shared.btn.cancel') }}
        </b-button>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import CustomerSelector from './CustomerSelector'
import reservationConfirmationSetup from './reservationConfirmationSetup'

export default {
  components: {
    CustomerSelector,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    selectedService: {
      type: Object,
      default: null,
    },
    selectedSpots: {
      type: Array,
      default: () => [],
    },
    selectedLocation: {
      type: String,
      default: null,
    },
    selectedCalendarId: {
      type: [Number, String],
      default: null,
    },
    tz: { type: String, required: true },
  },
  setup: reservationConfirmationSetup,
}
</script>
